/** @jsxRuntime classic */
/** @jsx jsx */
import { useState, useMemo } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { useDebounce } from 'react-use';
import { useNavigate } from 'react-router-dom';

import AutoComplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

import { SEARCH, useStore } from '../../store';
import { useSearchQuery } from '../../actions/SearchActions';
import { track } from '../../utils/track';

export default function Search({ maxWidth = '547px' }) {
  const { t } = useTranslation();
  const [search, setSearch] = useStore(SEARCH);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [open, setOpen] = useState(false);

  const { meetings, files, folders, meetingTexts } = useSearchQuery({
    search: debouncedSearch,
  });
  const navigate = useNavigate();

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    250,
    [search]
  );

  const options = useMemo(() => {
    return [
      ...folders.map((folder) => ({
        type: 'folder',
        type_label: t('Mappar/Projekt'),
        ...folder,
      })),
      ...meetings.map((meeting) => ({
        type: 'meeting',
        type_label: t('Filer'),
        ...meeting,
      })),
      ...files.map((file) => ({
        type: 'file',
        type_label: t('Filer'),
        ...file,
      })),
      ...meetingTexts.map((meetingText) => ({
        type: 'meeting_text',
        type_label: t('Transkriberingar'),
        ...meetingText,
      })),
      {
        type: 'search',
        name: t('Visa alla resultat'),
      },
    ];
  }, [meetings, folders, search]);

  const handleClick = (option) => {
    track('search_component_click', {
      type: option.type,
    });
    if (option.type === 'meeting') {
      navigate(`/meeting/${option.id}`);
    } else if (option.type === 'file') {
      navigate(`/file/${option.id}`);
    } else if (option.type === 'folder') {
      navigate(`/folders${option.folder_prefix}${option.id}`);
    } else if (option.type === 'meeting_text') {
      const match = option.text
        .filter((y) => y.highlight)
        .map((y) => y.text)
        .join(' ');
      navigate(`/transcript/${option.meeting_id}?q=${match || search}`);
    } else if (option.type === 'search') {
      navigate(`/search?q=${encodeURIComponent(search)}`);
    }
    setOpen(false);
  };

  const handleInputChange = (e, val) => {
    if (e?.key === 'Enter' || !e) {
      return;
    }
    setSearch(val);
    setOpen(true);
  };

  return (
    <div
      className="search-box"
      css={css`
        flex-grow: 1;
        max-width: ${maxWidth};
        display: flex;
        align-items: center;
      `}
    >
      <AutoComplete
        css={css`
          flex-grow: 1;
        `}
        freeSolo
        options={options}
        getOptionLabel={(option) => option.name || ''}
        groupBy={(option) => option.type_label}
        filterOptions={(options) => options}
        inputValue={search}
        open={open}
        blurOnSelect={true}
        onClose={() => {
          setOpen(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleClick(options[options.length - 1]);
          }
        }}
        onInputChange={handleInputChange}
        id="user-search2"
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('Sök efter filer eller mappar')}
            margin="normal"
            variant="outlined"
            size="small"
            autoFocus
            sx={{
              marginTop: '8px',
              background: '#fff',
              borderRadius: '8px',
            }}
            css={css`
              & fieldset {
                border-color: #e0e3e7 !important;
              }
            `}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment
                  position="start"
                  css={css`
                    padding-left: 0.5rem;
                  `}
                >
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
        renderOption={(params, option) => (
          <div {...params} key={option.id} onClick={() => handleClick(option)}>
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span
                css={css`
                  color: ${option.type === 'search'
                    ? 'var(--primary-main)'
                    : 'inherit'};
                  font-weight: ${option.type === 'search' ? 'bold' : 'inherit'};
                  font-size: ${option.type === 'search' ? '14px' : 'inherit'};
                `}
              >
                {option.name}
              </span>
              {option.text ? (
                <p
                  css={css`
                    font-size: 12px;
                  `}
                >
                  {option.text.map((y) => {
                    if (y.highlight) return <b>{y.text}</b>;
                    return y.text;
                  })}
                </p>
              ) : null}
            </div>
          </div>
        )}
      />
    </div>
  );
}
